import {PageFragment, PageFragmentProps, PageFragmentState} from "../../shared/PageFragment";
import {PageWithSidebarContainer} from "../../shared/PageWithSidebarContainer";
import React, {ReactElement} from "react";
import {SidebarItems} from "../../shared/Sidebar";
import {PathComponent} from "../../index";
import {Typography} from "@mui/material";
import {StyledBoxColumn, StyledContainer} from "../../shared/StyledComponents";
import {PD_XLG} from "../../shared/dimens";
import {FormGenContainer} from "../../shared/FormGenContainer";
import {
  AccountSecuritySettings,
  BaseSettings,
  GeneralSettings,
  NotificationsSettings,
  PreferencesSettings,
  Settings
} from "./settings";

abstract class BaseSettingsContentFragment<T extends BaseSettings> extends PageFragment {

  constructor(props: PageFragmentProps, context: any, readonly settings: T) {
    super(props, context);
  }

  renderContent(): React.ReactElement {
    return <StyledContainer>
      <StyledBoxColumn style={{padding: PD_XLG, gap: PD_XLG}}>
        <Typography>{this.getSettingsDescription()}</Typography>
        {this.renderSettingsContent()}
      </StyledBoxColumn>
    </StyledContainer>;
  }

  protected renderSettingsContent(): ReactElement {
    return <FormGenContainer
      autoSave
      onContainerSave={container => this.settings.save()}
      content={this.settings}/>
  }

  protected abstract getSettingsDescription(): string;
}

class GeneralSettingsContentFragment extends BaseSettingsContentFragment<GeneralSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().general);
  }

  protected getTitle(): string | null | undefined {
    return "General settings";
  }

  protected getSettingsDescription(): string {
    return "General account and client settings";
  }
}

class NotificationsSettingsContentFragment extends BaseSettingsContentFragment<NotificationsSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().notifications);
  }

  protected getTitle(): string | null | undefined {
    return "Notifications";
  }

  protected getSettingsDescription(): string {
    return "Manage which of the following types of notifications you want to receive";
  }
}


export class AccountSecurityFragment extends BaseSettingsContentFragment<AccountSecuritySettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().accountSecurity);
  }

  protected getTitle(): string | null | undefined {
    return "Account security";
  }

  protected getSettingsDescription(): string {
    return "Keep your account safe & secure";
  }
}

export class PreferencesFragment extends BaseSettingsContentFragment<PreferencesSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().preferences);
  }

  protected getTitle(): string | null | undefined {
    return "Preferences";
  }

  protected getSettingsDescription(): string {
    return "Manage your console preferences";
  }
}

const SIDEBAR_ITEMS: SidebarItems = {
  groups: [
    {
      items: [
        {
          title: "General",
          path: "general",
          render: pathProps => <GeneralSettingsContentFragment path={pathProps}/>,
        },
        {
          title: "Notifications",
          path: "notifications",
          render: pathProps => <NotificationsSettingsContentFragment path={pathProps}/>,
        },
      ],
      variant: "condensed",
    },
  ],
  containerId: "settings",
}

export type SettingsFragmentProps = PageFragmentProps & {}

type SettingsFragmentState = PageFragmentState & {}

export class SettingsFragment extends PageFragment<SettingsFragmentProps, SettingsFragmentState> {

  static nestedPaths(): PathComponent[] {
    return PageWithSidebarContainer.nestedPathsFromSidebarItems(SIDEBAR_ITEMS);
  }

  renderContent(): ReactElement {
    return <PageWithSidebarContainer path={this.props.path} size="sm" sidebarCollapsible sidebarItems={SIDEBAR_ITEMS}/>;
  }
}